import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sidebarArray } from '~/data/sidebar';
import { IChatRoom, ISidebar } from '~/types/common';
import { useGlobalContext } from '~/context/GlobalContext';
import { CameraIcon, ChatIcon, UsersIcon } from '../Icons/Icons';
import classNames from 'classnames';
import Notification from '~/components/PrivateChatContainer/Notification';
import styles from './TopBarContainer.module.css';

function TopBarContainer() {
  const { context, setContext } = useGlobalContext();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Referencje do mechanizmu bezszwowej karuzeli
  const wrapperRef = useRef<HTMLDivElement>(null);
  const firstTextRef = useRef<HTMLSpanElement>(null);
  const [isRouletteOverflow, setIsRouletteOverflow] = useState(false);

  const goToRoulette = () => {
    const userName = context.user?.name || 'guest';
    const gender = context.user?.gender || 'male';
    // Bez logOut — istniejąca sesja jest podtrzymana, a Websocket wyśle switchMode na zmianie trasy.
    navigate('/roulette', {
      state: {
        name: userName.startsWith('Gość_') ? 'guest' : userName,
        gender,
      }
    });
  };

  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (el) {
      const canScrollLeft = el.scrollLeft > 5;
      const canScrollRight = el.scrollLeft < (el.scrollWidth - el.clientWidth - 5);
      setShowLeftShadow(canScrollLeft);
      setShowRightShadow(canScrollRight);
    }
  };

  // Precyzyjne sprawdzanie dla bezszwowej karuzeli
  const checkRouletteOverflow = () => {
    const wrapper = wrapperRef.current;
    const textEl = firstTextRef.current;
    
    if (wrapper && textEl) {
      // Pobieramy prawdziwą szerokość pojedynczego tekstu i kontenera
      const textWidth = textEl.getBoundingClientRect().width;
      const wrapperWidth = wrapper.getBoundingClientRect().width;

      const hasOverflow = textWidth > wrapperWidth;
      setIsRouletteOverflow(hasOverflow);

      if (hasOverflow) {
        // Dystans przesunięcia = szerokość jednego napisu + wielkość przerwy (gap 20px)
        const travelDistance = textWidth + 20;
        // Ustawiamy zmienną CSS dla animacji karuzeli
        wrapper.style.setProperty('--travel-dist', `-${travelDistance}px`);
      }
    }
  };

  useEffect(() => {
    const activeRoomId = context.room?.id;
    if (activeRoomId && context.activeSidebarId === '') {
      const activeButton = document.getElementById(activeRoomId);
      if (activeButton && scrollRef.current) {
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
    setTimeout(checkScroll, 300);
  }, [context.room?.id, context.activeSidebarId]);

  useEffect(() => {
    checkScroll();
    checkRouletteOverflow();
    
    const handleResize = () => {
      checkScroll();
      checkRouletteOverflow();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [context.rooms]);

  const handleSidebarClick = (room: any) => {
    setContext({ activeSidebarId: room.id });
  };

  const handleRoomClick = (currentRoom: IChatRoom) => {
    setContext(prev => ({
      ...prev,
      activeSidebarId: '',
      room: { ...currentRoom, unreadCount: 0 },
      rooms: prev.rooms.map(r =>
        r.id === currentRoom.id ? { ...r, unreadCount: 0 } : r
      )
    }));
  };

  return (
    <div data-component="TopBarContainer" className={styles.topBarContainer}>

      <div className={classNames(styles.tab, styles.sidebarButtons)}>
        <button
          id="rooms"
          type="button"
          className={classNames(styles.sidebarButton, {
            [styles.active]: "rooms" === context.activeSidebarId,
          })}
          onClick={() => handleSidebarClick({ id: 'rooms', name: 'Pokoje' })}
        >
          <div className={styles.text}>Pokoje</div>
          <ChatIcon />
        </button>

        <button
          id="roulette"
          type="button"
          className={classNames(styles.sidebarButton, styles.rouletteSidebarButton)}
          onClick={goToRoulette}
          title="Wideo Ruletka"
        >
          {/* Nowy kontener maskujący (ucina wyjeżdżający tekst) */}
          <div className={styles.rouletteTextWrapper} ref={wrapperRef}>
            {/* Wózek (track) karuzeli, na którym poruszają się napisy */}
            <div 
              className={classNames(styles.carouselTrack, {
                [styles.scrollingText]: isRouletteOverflow
              })}
            >
              {/* Główny napis - jego szerokość jest mierzona */}
              <span ref={firstTextRef} className={styles.carouselText}>
                WYLOSUJ ROZMÓWCĘ
              </span>
              
              {/* Kopijujemy napis TYLKO gdy potrzebujemy karuzeli - to zapewnia płynną pętlę! */}
              {isRouletteOverflow && (
                <span className={styles.carouselText}>
                  WYLOSUJ ROZMÓWCĘ
                </span>
              )}
            </div>
          </div>
          <CameraIcon />
        </button>

        <button
          id="people"
          type="button"
          className={classNames(styles.sidebarButton, {
            [styles.active]: "people" === context.activeSidebarId,
          })}
          onClick={() => handleSidebarClick({ id: 'people', name: 'Osoby' })}
        >
          <div className={styles.text}>Osoby</div>
          <UsersIcon />
        </button>
      </div>

      <div className={styles.scrollWrapper}>
        <div className={classNames(styles.shadowLeft, { [styles.visible]: showLeftShadow })} />

        <div
          className={classNames(styles.tab, styles.roomButtons)}
          ref={scrollRef}
          onScroll={checkScroll}
        >
          {context.rooms.map((room: IChatRoom) => {
            if (room.private) return null;
            const isActive = room.id === (context.room && context.activeSidebarId === '' ? context.room.id : false);

            return (
              <button
                key={room.id}
                id={room.id}
                className={classNames(styles.button, {
                  [styles.active]: isActive,
                })}
                onClick={() => handleRoomClick(room)}
              >
                <span className={classNames(styles.text, styles.name)}>
                  {room.name}
                </span>
                <Notification
                  roomId={room.id}
                  count={room.unreadCount}
                />
              </button>
            );
          })}
        </div>
        <div className={classNames(styles.shadowRight, { [styles.visible]: showRightShadow })} />
      </div>

    </div>
  );
}

export default TopBarContainer;